import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { FixedSizeList as List } from 'react-window';
import { CmdLoader } from '@commander-services/gui-components';
import { useQueryClient } from '@tanstack/react-query';
import { IDetailWaypointResponseData, IVerifyDeleteRequestData, IWaypoint } from './types';
import PermissionsService, {
  PERMISSION_WAYPOINT_CUSTOMER,
  PERMISSION_WAYPOINT_PRIVATE,
} from '../../../Services/PermissionsService';
import {
  handlePositionIcon,
  deleteWaypoint,
  verifyRemoveWaypoint,
  filterBinary,
} from './WaypointService';
import ConfirmDialog from '../../ConfirmDialog';
import { customersAtom, selectedCustomersAtom } from '../../../store/recoil/customers';
import { WAYPOINT_QUERY_KEY, WAYPOINT_WORD_LENGTH } from './constants';
import WaypointIconService from './WaypointIconService';
import {
  activeFilterAtom,
  detailWaypointAtom,
  filteredWaypointsAtom,
  filteredWaypointsStringAtom,
  isWaypointsOpenAtom,
  onHoverWaypointIdAtom,
  removedWaypointIdAtom,
  showWaypointLoaderAtom,
  waypointDataAtom,
  waypointIdAtom,
  waypointsAtom,
} from './WaypointState';
import { autofocusAtom } from '../OnlineMapState';
import useAnalytics from '../../../hooks/useAnalytics';

const WAYPOINT_VERIFY_TYPE_ALARM = 'alarm';
const WAYPOINT_PRIVATE = 'private';
const WAYPOINT_CUSTOMER = 'customer';

interface IWaypointForm {
  filterWaypointOpen: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  detailWaypointOpen: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  addWaypointOpen: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  editWaypointOpen: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  copyWaypointOpen: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
}

function WaypointForm(props: IWaypointForm) {
  const { formatMessage: f } = useIntl();
  const [isOpenConfirmDialog, setIsOpenConfirmDialog] = React.useState(false);
  const [waypoints, setWaypoints] = useRecoilState<IWaypoint[]>(waypointsAtom);
  const [waypointData, setWaypointData] = useRecoilState<IDetailWaypointResponseData | null>(
    waypointDataAtom
  );
  const setRemovedWaypointId = useSetRecoilState<number | null>(removedWaypointIdAtom);
  const setOnHoverWaypointId = useSetRecoilState<number | null>(onHoverWaypointIdAtom);
  const [filteredWaypointsString, setFilteredWaypointsString] = useRecoilState<string>(
    filteredWaypointsStringAtom
  );
  const [filteredWaypoints, setFilteredWaypoints] =
    useRecoilState<IWaypoint[]>(filteredWaypointsAtom);
  const [activeFilter, setActiveFilter] = useRecoilState(activeFilterAtom);

  const [isWaypointsOpen] = useRecoilState<boolean>(isWaypointsOpenAtom);
  const [detailWaypoint, setDetailWaypoint] = useRecoilState<IDetailWaypointResponseData | null>(
    detailWaypointAtom
  );

  // const [scroolToIndexList, setScroolToIndexList] = React.useState<number | undefined>(undefined);
  const [verifyDeleteData, setVerifyDeleteData] = React.useState<IVerifyDeleteRequestData | null>(
    null
  );

  const [waypointReferenceTitle, setWaypointReferenceTitle] = React.useState<string[]>([]);
  const [selectedWaypointId, setWaypointId] = useRecoilState<number | null>(waypointIdAtom);
  const showWaypointLoader = useRecoilValue(showWaypointLoaderAtom);
  const queryClient = useQueryClient();

  const setAutofocus = useSetRecoilState<boolean>(autofocusAtom);
  const listRef = React.createRef<List>();
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const filteredCustomerLabel = useRecoilValue(customersAtom)
    .filter((customer) => selectedCustomers.includes(customer.id))
    .map((customer) => customer.label);
  const { trackEvent } = useAnalytics();

  const handleClickWaypoint = React.useCallback(
    async (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      const waypointId = event.currentTarget.getAttribute('data-id');
      trackEvent(['map', 'click', 'Select waypoint from list']);

      if (String(waypointId) !== String(selectedWaypointId)) {
        setWaypointId(Number(waypointId));
      } else {
        setWaypointId(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedWaypointId]
  );

  const handleMouseEnter = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    const waypointId = event.currentTarget.getAttribute('data-id');
    setOnHoverWaypointId(Number(waypointId));
  };

  const handleMouseLeave = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    setOnHoverWaypointId(null);
  };

  const handleOnChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    trackEvent(['map', 'click', 'Search Waypoint']);
    setFilteredWaypointsString(event.currentTarget.value);
    setAutofocus(true);
  };

  const handleCancelSearch = () => {
    setFilteredWaypointsString('');
  };

  const updateWaypoints = () => {
    queryClient.invalidateQueries({
      queryKey: [WAYPOINT_QUERY_KEY, selectedCustomers],
    });
  };

  const renderRow = ({ index, style }) => {
    return (
      <a
        id={String(waypoints[index].id)}
        key={String(waypoints[index].id)}
        data-id={waypoints[index].id}
        href="#"
        style={style}
        className={`${waypoints[index].id === selectedWaypointId ? 'item active pl-4' : 'item pl-4'}`}
        onClick={handleClickWaypoint}
        // onMouseEnter={handleMouseEnter}
        // onMouseLeave={handleMouseLeave}
        title={waypoints[index].name}
      >
        <i
          className={`cmd-icon cmd-icon-${WaypointIconService.getCssIcon(
            waypoints[index].icon || ''
          )} pr-2`}
        />
        {waypoints[index] &&
        waypoints[index].name &&
        String(waypoints[index].name).length > WAYPOINT_WORD_LENGTH
          ? `${String(waypoints[index].name).substring(0, WAYPOINT_WORD_LENGTH)} ...`
          : waypoints[index].name}
        <span className="ml-auto text-right">
          {waypoints[index].type && waypoints[index].type === 'private' && (
            <i
              className="cmd-icon cmd-icon-preview-2 "
              style={{
                position: 'absolute',
                left: handlePositionIcon(filteredCustomerLabel),
                top: '50%',
                transform: 'translateY(-50%)',
              }}
            />
          )}
          {` ${waypoints[index].customerLabel}`}
        </span>
      </a>
    );
  };

  const renderSearchRow = ({ index, style }) => {
    return (
      <a
        id={String(filteredWaypoints[index].id)}
        key={String(filteredWaypoints[index].id)}
        data-id={filteredWaypoints[index].id}
        href="#"
        style={style}
        className={`${filteredWaypoints[index].id === selectedWaypointId ? 'item active pl-4' : 'item pl-4'}`}
        onClick={handleClickWaypoint}
        // onMouseEnter={handleMouseEnter}
        // onMouseLeave={handleMouseLeave}
        title={filteredWaypoints[index].name}
      >
        <i
          className={`cmd-icon cmd-icon-${WaypointIconService.getCssIcon(
            filteredWaypoints[index].icon || ''
          )} pr-2`}
        />
        {filteredWaypoints[index] &&
        filteredWaypoints[index].name &&
        String(filteredWaypoints[index].name).length > WAYPOINT_WORD_LENGTH
          ? `${String(filteredWaypoints[index].name).substring(0, WAYPOINT_WORD_LENGTH)} ...`
          : filteredWaypoints[index].name}
        <span className="ml-auto text-right">
          {filteredWaypoints[index].type && filteredWaypoints[index].type === 'private' && (
            <i
              className="cmd-icon cmd-icon-preview-2"
              style={{
                position: 'absolute',
                left: handlePositionIcon(filteredCustomerLabel, true),
                top: '50%',
                transform: 'translateY(-50%)',
              }}
            />
          )}
          {` ${filteredWaypoints[index].customerLabel}`}
        </span>
      </a>
    );
  };

  const handleDeleteWaypoint = async (_event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (!waypointData || !selectedWaypointId) {
      return;
    }
    setWaypoints([]);
    setIsOpenConfirmDialog(false);
    setWaypointId(null);
    // dispatch(onlineMapActions.setWaypointId(false));

    const response = await deleteWaypoint(selectedWaypointId);

    updateWaypoints();
    // setWaypointsLoaded(false);
    if (response) {
      setVerifyDeleteData(null);
      setRemovedWaypointId(selectedWaypointId);
      setOnHoverWaypointId(null);
    }
  };

  const handleConfirmDialog = async () => {
    trackEvent(['map', 'click', 'Delete waypoint']);
    if (detailWaypoint) {
      setWaypointData(detailWaypoint);
    }

    if (typeof selectedWaypointId === 'number') {
      setWaypointReferenceTitle([]);
      const verifyDelete = await verifyRemoveWaypoint(selectedWaypointId);

      if (verifyDelete) {
        const findType = verifyDelete.details.find(
          (item) => item.type === WAYPOINT_VERIFY_TYPE_ALARM
        );
        if (findType) {
          const tempTitle = findType.details.map((item) => item.name);
          setWaypointReferenceTitle(tempTitle);
        }

        setVerifyDeleteData(verifyDelete);
        setIsOpenConfirmDialog(true);
      }
    }
  };

  const handleCloseConfirmDialog = () => {
    setIsOpenConfirmDialog(false);
  };

  const handleShowSelectedElement = React.useCallback(() => {
    if (typeof selectedWaypointId === 'number' && isWaypointsOpen) {
      const scrollToIndex = waypoints.findIndex((index) => index.id === selectedWaypointId);

      if (listRef.current) {
        listRef.current.scrollToItem(scrollToIndex, 'smart');
      }
      // setScroolToIndexList(waypoints.findIndex((index) => index.id === selectedWaypointId));
    }
    // else {
    // setScroolToIndexList(undefined);
    // }
  }, [isWaypointsOpen, listRef, selectedWaypointId, waypoints]);

  React.useEffect(() => {
    handleShowSelectedElement();
  }, [handleShowSelectedElement]);

  React.useEffect(() => {
    setFilteredWaypoints(filterBinary(filteredWaypointsString, waypoints));
  }, [filteredWaypointsString, setFilteredWaypoints, waypoints]);

  const handleCreatePermissions = (): boolean => {
    let tempPermissions = 0;

    if (isWaypointsOpen) {
      selectedCustomers.forEach((item) => {
        const permission =
          PermissionsService.hasAccess(PERMISSION_WAYPOINT_CUSTOMER, ['create'], item) ||
          PermissionsService.hasAccess(PERMISSION_WAYPOINT_PRIVATE, ['create'], item);

        if (permission) {
          tempPermissions += 1;
        }
      });
    }

    if (tempPermissions > 0) {
      return true;
    }

    return false;
  };

  const handlePermissions = (
    detailWp: IDetailWaypointResponseData,
    permissionType: string
  ): boolean => {
    if (detailWp && isWaypointsOpen) {
      if (detailWp.type === WAYPOINT_CUSTOMER) {
        return PermissionsService.hasAccess(
          PERMISSION_WAYPOINT_CUSTOMER,
          [permissionType],
          Number(detailWp.customerId)
        );
      }
      if (detailWp.type === WAYPOINT_PRIVATE) {
        return PermissionsService.hasAccess(
          PERMISSION_WAYPOINT_PRIVATE,
          [permissionType],
          Number(detailWp.customerId)
        );
      }
    }
    return false;
  };

  return (
    <>
      {isOpenConfirmDialog && waypointData && waypointData.name && (
        <ConfirmDialog
          title={`${f(
            {
              id: 'waypoint.confirm.dialog.title',
            },
            { name: waypointData.name }
          )}`}
          cancelCallback={handleCloseConfirmDialog}
          continueCallback={handleDeleteWaypoint}
          cancelButtonTitle={f({
            id: 'waypoint.confirm.dialog.cancel',
          })}
          continueButtonTitle={f({
            id: 'waypoint.confirm.dialog.confirm',
          })}
          tooltip={waypointReferenceTitle.join('\n')}
        >
          {verifyDeleteData && verifyDeleteData.details[0]
            ? `${verifyDeleteData.details[0].message} ${waypointReferenceTitle.join(',\n')}`
            : f(
                {
                  id: 'waypoint.confirm.dialog.message',
                },
                { name: waypointData.name }
              )}
        </ConfirmDialog>
      )}
      <div className="b-mc-waypoints">
        <div className="b-mc-waypoints-filter">
          <div className="row align-items-center">
            <div className="col pr-2">
              <div className="w-form-control">
                <input
                  type="text"
                  className="form-control"
                  placeholder={f({ id: 'waypoints.search.placeholder' })}
                  value={filteredWaypointsString}
                  onChange={handleOnChangeSearch}
                  data-cy="Waypoints-search"
                />
                <i className="cmd-icon cmd-icon-search" />
                {filteredWaypointsString.length > 0 && (
                  <a onClick={handleCancelSearch} className="cmd-icon cmd-icon-remove remove">
                    {/* &nbsp; */}
                  </a>
                )}
              </div>
            </div>
            <div className="col-auto pl-0">
              <a
                href="#"
                className={`e-button-sq border-on ${activeFilter ? 'bubble-on' : ''}`}
                onClick={props.filterWaypointOpen}
              >
                <i className="cmd-icons cmd-icon-filter" />
                <span className="sr-only">{f({ id: 'waypoints.tooltip.filter.waypoint' })}</span>
              </a>
            </div>
          </div>

          <div className="b-mc-waypoints-filter-actions">
            {isWaypointsOpen && handleCreatePermissions() ? (
              <button
                type="button"
                className="e-button-sq"
                title={f({ id: 'waypoints.tooltip.add.waypoint' })}
                onClick={props.addWaypointOpen}
                data-cy="add_waypoint"
              >
                <i className="cmd-icons cmd-icon-add" />
                <span className="sr-only">{f({ id: 'waypoints.tooltip.add.waypoint' })}</span>
              </button>
            ) : (
              isWaypointsOpen &&
              (PermissionsService.hasAccess(
                PERMISSION_WAYPOINT_CUSTOMER,
                ['read'],
                selectedCustomers
              ) ||
                PermissionsService.hasAccess(
                  PERMISSION_WAYPOINT_PRIVATE,
                  ['read'],
                  selectedCustomers
                )) && (
                <a
                  href="#"
                  className="e-button-sq e-button-sq--disabled"
                  title={f({ id: 'waypoints.tooltip.add.waypoint' })}
                  data-cy="add_waypoint_disabled"
                >
                  <i className="cmd-icons cmd-icon-add" />
                  <span className="sr-only">{f({ id: 'waypoints.tooltip.add.waypoint' })}</span>
                </a>
              )
            )}
          </div>
        </div>
        <div className="b-waypoints-list">
          {/* <InfiniteLoader isRowLoaded={waypointsLoaded}>
            {() => ( */}
          {/* <ScrollSync>
            {({ onScroll }) => ( */}
          {filteredWaypointsString.length > 0 && filteredWaypoints.length === 0 && (
            <a href="#" className="item">
              {f({ id: 'waypoints.missing' })}
            </a>
          )}
          {waypoints.length === 0 && (
            <a href="#" className="item">
              {f({ id: 'waypoints.missing' })}
            </a>
          )}
          <div className="b-waypoints-list-items">
            {showWaypointLoader && isWaypointsOpen && <CmdLoader inContent={true} />}

            {showWaypointLoader ? (
              <a href="#" className="item">
                {f({ id: 'waypoints.loading' })}
              </a>
            ) : filteredWaypointsString.length > 0 ? (
              <List
                width={300}
                height={340}
                // rowRenderer={renderSearchRow}
                itemCount={filteredWaypoints.length}
                // overscanRowCount={50}
                itemSize={35}
                // scrollToIndex={scroolToIndexList}
                ref={listRef}
              >
                {renderSearchRow}
              </List>
            ) : (
              <List
                width={300}
                height={340}
                // rowRenderer={renderRow}
                itemCount={waypoints.length}
                // overscanRowCount={50}
                itemSize={35}
                // scrollToIndex={scroolToIndexList}
                ref={listRef}
              >
                {renderRow}
              </List>
            )}
          </div>
          {/* )}
          </ScrollSync> */}
          {/* )} */}
          {/* </InfiniteLoader>  */}
          {selectedWaypointId && (
            <div className="b-waypoints-list-actions">
              <a
                href="#"
                className="e-button-sq"
                title={f({ id: 'waypoints.tooltip.detail.waypoint' })}
                onClick={props.detailWaypointOpen}
                data-cy="waypoints_detail"
              >
                <i className="cmd-icons cmd-icon-detail" />
                <span className="sr-only">{f({ id: 'waypoints.tooltip.detail.waypoint' })}</span>
              </a>
              {detailWaypoint && handlePermissions(detailWaypoint, 'update') && (
                <a
                  href="#"
                  className="e-button-sq"
                  title={f({ id: 'waypoints.tooltip.edit.waypoint' })}
                  onClick={props.editWaypointOpen}
                  data-cy="waypoints_edit"
                >
                  <i className="cmd-icons cmd-icon-edit" />
                  <span className="sr-only">{f({ id: 'waypoints.tooltip.edit.waypoint' })}</span>
                </a>
              )}
              {detailWaypoint && handlePermissions(detailWaypoint, 'create') && (
                <a
                  href="#"
                  className="e-button-sq"
                  data-cy="waypoints_copy"
                  title={f({ id: 'waypoints.tooltip.copy.waypoint' })}
                  onClick={props.copyWaypointOpen}
                >
                  <i className="cmd-icons cmd-icon-duplicate" />
                  <span className="sr-only">{f({ id: 'waypoints.tooltip.copy.waypoint' })}</span>
                </a>
              )}
              {detailWaypoint && handlePermissions(detailWaypoint, 'delete') && (
                <a
                  href="#"
                  className="e-button-sq"
                  title={f({ id: 'waypoints.tooltip.delete.waypoint' })}
                  onClick={handleConfirmDialog}
                  data-cy="waypoints_delete"
                >
                  <i className="cmd-icons cmd-icon-bin" />
                  <span className="sr-only">{f({ id: 'waypoints.tooltip.delete.waypoint' })}</span>
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default React.memo(WaypointForm);
